import React, { useState, useEffect, useCallback, useRef } from "react";
import { encoderLabel } from "../../services/videoCompressionService";
import { useAutoUpload } from "../../context/AutoUploadContext";
import { useUpload } from "../../context/UploadContext";
import { useAuth } from "../../context/AuthContext";
import { getEvents } from "../../services/eventService";
import { getGalleriesByEvent } from "../../services/galleryService";
import { getMyChatRooms } from "../../services/chatService";
import { getLatestPSSVersion } from "../../services/configService";
import Button from "../../components/common/Button";
import Loader from "../../components/common/Loader";
import CreateGalleryModal from "../../components/common/CreateGalleryModal";
import CreateAlbumModal from "../../components/common/CreateAlbumModal";
import PSSUpdateBanner from "../../components/common/PSSUpdateBanner";
import { formatFileSize, resolveUserCountryCode } from "../../utils/helpers";
import { toast } from "react-toastify";
import PrivacyPolicyModal, { hasAcceptedPrivacy } from "../../components/common/PrivacyPolicyModal";

const INTERVAL_OPTIONS = [
  { label: "1 minute", value: 1 * 60 * 1000 },
  { label: "5 minutes", value: 5 * 60 * 1000 },
  { label: "15 minutes", value: 15 * 60 * 1000 },
  { label: "30 minutes", value: 30 * 60 * 1000 },
  { label: "1 hour", value: 60 * 60 * 1000 },
  { label: "2 hours", value: 2 * 60 * 60 * 1000 },
  { label: "4 hours", value: 4 * 60 * 60 * 1000 },
];

const isFileSystemSupported = typeof window !== "undefined" && "showDirectoryPicker" in window;

const formatTime = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  if (isToday) return time;
  const dateStr = d.toLocaleDateString("en-US", {
    month: "short", day: "numeric",
  });
  return `${dateStr}, ${time}`;
};

const formatInterval = (ms) => {
  const opt = INTERVAL_OPTIONS.find((o) => o.value === ms);
  return opt ? opt.label : `${Math.round(ms / 60000)} min`;
};

const formatEta = (seconds) => {
  if (seconds == null) return "";
  if (seconds < 5) return "a few seconds left";
  if (seconds < 60) return `${seconds}s left`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s left` : `${mins}m left`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m left` : `${hrs}h left`;
};

const statusIcon = (status, fileType) => {
  switch (status) {
    case "checking": return "🔍";
    case "compressing": return "⚙️";
    case "uploading": return "📡";
    case "retrying": return "🔁";
    case "done": return "✅";
    case "failed": return "❌";
    default: return fileType?.startsWith("video/") ? "🎬" : "🖼️";
  }
};

const compressionStatusText = (item) => {
  const pct = Math.max(0, Math.min(100, Number(item.compressProgress || 0)));
  if (item.fileType?.startsWith("video/") && pct <= 30) {
    return `Copying to Studio ${pct}%`;
  }
  return `Compressing ${pct}%`;
};

const fileStatusText = (item) => {
  switch (item.status) {
    case "checking": return "Analysing...";
    case "compressing": return compressionStatusText(item);
    case "uploading": return `Uploading ${item.uploadProgress}%${item.eta ? " — " + formatEta(item.eta) : ""}`;
    case "retrying": return "Waiting for network — will retry...";
    case "done": return "Done";
    case "failed": return item.error || "Failed";
    default: return item.queuePosition ? `Queue #${item.queuePosition}` : "Waiting";
  }
};

/* ─── Edit Job Modal ─── */
const EditJobModal = ({ job, onClose, onSave }) => {
  const [editName, setEditName] = useState(job.jobName || "");
  const [editSourceType, setEditSourceType] = useState(job.sourceType || "event");
  const [editEvents, setEditEvents] = useState([]);
  const [editRooms, setEditRooms] = useState([]);
  const [editSourceId, setEditSourceId] = useState(job.sourceId || "");
  const [editGalleries, setEditGalleries] = useState([]);
  const [editGalleryId, setEditGalleryId] = useState(String(job.gallery?.Id || ""));
  const [editAlbumId, setEditAlbumId] = useState(String(job.album?.Id || ""));
  const [editIntervalMs, setEditIntervalMs] = useState(job.intervalMs);
  const [editDirHandle, setEditDirHandle] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingGalleries, setLoadingGalleries] = useState(false);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);

  // Load events & rooms on mount
  useEffect(() => {
    setLoadingEvents(true);
    getEvents()
      .then((res) => setEditEvents(res.data?.Result || res.data || []))
      .catch(() => {})
      .finally(() => setLoadingEvents(false));

    setLoadingRooms(true);
    getMyChatRooms("Personal")
      .then((res) => setEditRooms(res.data?.Result || res.data || []))
      .catch(() => {})
      .finally(() => setLoadingRooms(false));
  }, []);

  // Load galleries when source changes
  useEffect(() => {
    if (!editSourceId) { setEditGalleries([]); return; }
    setLoadingGalleries(true);
    getGalleriesByEvent(editSourceId)
      .then((res) => {
        const gals = res.data?.Result || [];
        setEditGalleries(gals);
        // If current gallery exists in new list, keep it; otherwise reset
        if (!gals.find((g) => String(g.Id) === editGalleryId)) {
          setEditGalleryId("");
          setEditAlbumId("");
        }
      })
      .catch(() => toast.error("Failed to load galleries"))
      .finally(() => setLoadingGalleries(false));
  }, [editSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset album when gallery changes
  useEffect(() => {
    const gal = editGalleries.find((g) => String(g.Id) === editGalleryId);
    if (gal) {
      const albumExists = (gal.Albums || []).find((a) => String(a.Id) === editAlbumId);
      if (!albumExists) setEditAlbumId("");
    }
  }, [editGalleryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset source selection when source type changes (but not on initial render)
  const initialRef = useRef(true);
  useEffect(() => {
    if (initialRef.current) { initialRef.current = false; return; }
    setEditSourceId("");
    setEditGalleries([]);
    setEditGalleryId("");
    setEditAlbumId("");
  }, [editSourceType]);

  const editSelectedGallery = editGalleries.find((g) => String(g.Id) === editGalleryId);
  const editAlbums = editSelectedGallery?.Albums || [];
  const editSelectedAlbum = editAlbums.find((a) => String(a.Id) === editAlbumId);

  const handleChangeFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: "read" });
      setEditDirHandle(handle);
      setEditFolderName(handle.name);
    } catch (err) {
      if (err.name !== "AbortError") toast.error("Failed to select folder.");
    }
  };

  const handleSave = () => {
    const updates = { jobId: job.id, jobName: editName.trim() || job.jobName, intervalMs: editIntervalMs };

    if (editDirHandle) {
      updates.dirHandle = editDirHandle;
      updates.folderName = editFolderName;
    }

    if (editSelectedGallery && editSelectedAlbum) {
      updates.gallery = editSelectedGallery;
      updates.album = editSelectedAlbum;
      updates.chatRoomId = editSelectedGallery.ChatRoomId || editSourceId;
      updates.sourceType = editSourceType;
      updates.sourceId = editSourceId;
    }

    onSave(updates);
  };

  // Did anything actually change?
  const galleryChanged = editSelectedGallery && editSelectedAlbum &&
    (String(editSelectedGallery.Id) !== String(job.gallery?.Id) || String(editSelectedAlbum.Id) !== String(job.album?.Id));
  const hasChanges = editName.trim() !== job.jobName || editIntervalMs !== job.intervalMs || editDirHandle || galleryChanged;

  return (
    <div style={editStyles.overlay} onClick={onClose}>
      <div style={editStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={editStyles.header}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e1e2d" }}>Edit Job</h3>
          <button style={editStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Job Name */}
        <div style={styles.field}>
          <label style={styles.label}>Job Name</label>
          <input
            style={styles.select}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </div>

        {/* Folder */}
        <div style={styles.field}>
          <label style={styles.label}>Local Folder</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Button onClick={handleChangeFolder} variant="secondary" style={{ padding: "8px 16px", fontSize: 13 }}>
              📂 Change Folder
            </Button>
            <span style={styles.folderName}>
              {editDirHandle ? editFolderName : job.folderName}
            </span>
          </div>
        </div>

        {/* Current destination (info-only) */}
        <div style={{ ...styles.field, padding: "10px 14px", background: "#f8f8fc", borderRadius: 8, marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 4 }}>
            Current Destination
          </div>
          <div style={{ fontSize: 14, color: "#333" }}>
            {job.gallery?.Name} → {job.album?.Name}
          </div>
        </div>

        {/* Source Type Toggle */}
        <div style={styles.field}>
          <label style={styles.label}>Change Destination (optional)</label>
          <div style={styles.toggleGroup}>
            <button
              type="button"
              style={{ ...styles.toggleBtn, ...(editSourceType === "event" ? styles.toggleBtnActive : {}) }}
              onClick={() => setEditSourceType("event")}
            >
              🎉 Event
            </button>
            <button
              type="button"
              style={{ ...styles.toggleBtn, ...(editSourceType === "room" ? styles.toggleBtnActive : {}) }}
              onClick={() => setEditSourceType("room")}
            >
              💬 Room
            </button>
          </div>
        </div>

        {/* Event / Room dropdown */}
        {editSourceType === "event" ? (
          <div style={styles.field}>
            <label style={styles.label}>Event</label>
            <select
              style={styles.select}
              value={editSourceId}
              onChange={(e) => setEditSourceId(e.target.value)}
              disabled={loadingEvents}
            >
              <option value="">{loadingEvents ? "Loading..." : "— Select Event —"}</option>
              {editEvents.map((ev) => (
                <option key={ev.Id} value={ev.Id}>{ev.Name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={styles.field}>
            <label style={styles.label}>Room</label>
            <select
              style={styles.select}
              value={editSourceId}
              onChange={(e) => setEditSourceId(e.target.value)}
              disabled={loadingRooms}
            >
              <option value="">{loadingRooms ? "Loading..." : "— Select Room —"}</option>
              {editRooms.map((r) => (
                <option key={r.Id} value={r.Id}>{r.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Gallery */}
        <div style={styles.field}>
          <label style={styles.label}>Gallery</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={editGalleryId}
              onChange={(e) => setEditGalleryId(e.target.value)}
              disabled={!editSourceId || loadingGalleries}
            >
              <option value="">
                {!editSourceId ? `— Select ${editSourceType} first —` : loadingGalleries ? "Loading..." : "— Select Gallery —"}
              </option>
              {editGalleries.map((g) => (
                <option key={g.Id} value={g.Id}>{g.Name}</option>
              ))}
            </select>
            {editSourceId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateGallery(true)}
                style={styles.inlineCreateBtn}
                title="Create new gallery"
              >
                + New
              </Button>
            )}
          </div>
        </div>

        {/* Album */}
        <div style={styles.field}>
          <label style={styles.label}>Album</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={editAlbumId}
              onChange={(e) => setEditAlbumId(e.target.value)}
              disabled={!editGalleryId || editAlbums.length === 0}
            >
              <option value="">
                {!editGalleryId
                  ? "— Select gallery first —"
                  : editAlbums.length === 0
                  ? "— No albums —"
                  : "— Select Album —"}
              </option>
              {editAlbums.map((a) => (
                <option key={a.Id} value={a.Id}>{a.Name} ({a.ImageCount || 0} files)</option>
              ))}
            </select>
            {editGalleryId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateAlbum(true)}
                style={styles.inlineCreateBtn}
                title="Create new album"
              >
                + New
              </Button>
            )}
          </div>
        </div>

        {/* Interval */}
        <div style={styles.field}>
          <label style={styles.label}>Scan Interval</label>
          <select
            style={styles.select}
            value={editIntervalMs}
            onChange={(e) => setEditIntervalMs(Number(e.target.value))}
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges} style={{ flex: 1 }}>
            💾 Save Changes
          </Button>
        </div>

        {/* Create Gallery Modal (inside edit) */}
        {showCreateGallery && (
          <CreateGalleryModal
            eventId={editSourceId}
            onClose={() => setShowCreateGallery(false)}
            onCreated={(newGallery) => {
              setLoadingGalleries(true);
              getGalleriesByEvent(editSourceId)
                .then((res) => {
                  const updated = res.data?.Result || [];
                  setEditGalleries(updated);
                  const created = updated.find((g) => g.Name === newGallery?.Name) || updated[updated.length - 1];
                  if (created) setEditGalleryId(String(created.Id));
                })
                .catch(() => toast.error("Failed to refresh galleries"))
                .finally(() => setLoadingGalleries(false));
            }}
          />
        )}

        {/* Create Album Modal (inside edit) */}
        {showCreateAlbum && editSelectedGallery && (
          <CreateAlbumModal
            galleryId={editSelectedGallery.Id}
            galleryName={editSelectedGallery.Name}
            onClose={() => setShowCreateAlbum(false)}
            onCreated={(newAlbum) => {
              setLoadingGalleries(true);
              getGalleriesByEvent(editSourceId)
                .then((res) => {
                  const updated = res.data?.Result || [];
                  setEditGalleries(updated);
                  const gal = updated.find((g) => String(g.Id) === editGalleryId);
                  if (gal) {
                    const created = (gal.Albums || []).find((a) => a.Name === newAlbum?.Name)
                      || (gal.Albums || [])[(gal.Albums || []).length - 1];
                    if (created) setEditAlbumId(String(created.Id));
                  }
                })
                .catch(() => toast.error("Failed to refresh albums"))
                .finally(() => setLoadingGalleries(false));
            }}
          />
        )}
      </div>
    </div>
  );
};

const editStyles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: "#fff", borderRadius: 14, padding: 28, width: "100%",
    maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 22, paddingBottom: 14, borderBottom: "1px solid #f0f0f0",
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 18, color: "#999",
    cursor: "pointer", padding: "4px 8px", borderRadius: 6,
  },
};

const AutoUpload = () => {
  const { user } = useAuth();
  const { jobs, loaded, createJob, updateJob, pauseJob, resumeJob, removeJob, scanNow } = useAutoUpload();
  const { sessions } = useUpload();

  // PSS version check
  const [pssInstalledVersion, setPssInstalledVersion] = useState(null);
  const [pssLatestVersion, setPssLatestVersion] = useState(null);
  useEffect(() => {
    getLatestPSSVersion()
      .then((res) => { if (res.data?.version) setPssLatestVersion(res.data.version); })
      .catch(() => {});
    import("../../services/videoCompressionService").then((mod) => {
      mod.checkCompanion().then(() => {
        setPssInstalledVersion(mod.getCompanionVersion?.() ?? null);
      });
    });
  }, []);

  // Setup form state
  const [jobName, setJobName] = useState("");
  const [dirHandle, setDirHandle] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [sourceType, setSourceType] = useState("event"); // "event" | "room"
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [selectedAlbumId, setSelectedAlbumId] = useState("");
  const [intervalMs, setIntervalMs] = useState(INTERVAL_OPTIONS[1].value); // default 30 min
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingGalleries, setLoadingGalleries] = useState(false);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);

  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [editingJob, setEditingJob] = useState(null); // job object being edited
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Track completed session groups to hide progress after 3s
  const [hiddenProgress, setHiddenProgress] = useState({}); // { [jobId]: true }

  // Countdown timers — re-render every second
  const [, setTick] = useState(0);
  const hasRunningJobs = Object.values(jobs).some((j) => j.status === "running");
  useEffect(() => {
    if (!hasRunningJobs) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [hasRunningJobs]);

  // The active source ID (either eventId or roomId)
  const activeSourceId = sourceType === "event" ? selectedEventId : selectedRoomId;

  // Load events on mount
  useEffect(() => {
    setLoadingEvents(true);
    getEvents()
      .then((res) => setEvents(res.data?.Result || res.data || []))
      .catch(() => toast.error("Failed to load events"))
      .finally(() => setLoadingEvents(false));
  }, []);

  // Load rooms on mount
  useEffect(() => {
    setLoadingRooms(true);
    getMyChatRooms("Personal")
      .then((res) => setRooms(res.data?.Result || res.data || []))
      .catch(() => toast.error("Failed to load rooms"))
      .finally(() => setLoadingRooms(false));
  }, []);

  // Reset selections when source type changes
  useEffect(() => {
    setSelectedEventId("");
    setSelectedRoomId("");
    setGalleries([]);
    setSelectedGalleryId("");
    setSelectedAlbumId("");
  }, [sourceType]);

  // Load galleries when source changes (event or room)
  useEffect(() => {
    if (!activeSourceId) { setGalleries([]); return; }
    setLoadingGalleries(true);
    setSelectedGalleryId("");
    setSelectedAlbumId("");
    getGalleriesByEvent(activeSourceId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error("Failed to load galleries"))
      .finally(() => setLoadingGalleries(false));
  }, [activeSourceId]);

  // Reset album when gallery changes
  useEffect(() => { setSelectedAlbumId(""); }, [selectedGalleryId]);

  const selectedGallery = galleries.find((g) => String(g.Id) === String(selectedGalleryId));
  const albums = selectedGallery?.Albums || [];
  const selectedAlbum = albums.find((a) => String(a.Id) === String(selectedAlbumId));
  const countryCode = resolveUserCountryCode(user, null);

  const handleSelectFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: "read" });
      setDirHandle(handle);
      setFolderName(handle.name);
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error("Failed to select folder.");
      }
    }
  };

  const canStart = dirHandle && selectedGallery && selectedAlbum && intervalMs;

  const doCreateJob = () => {
    if (!canStart) return;

    createJob({
      jobName: jobName.trim() || folderName,
      dirHandle,
      folderName,
      gallery: selectedGallery,
      album: selectedAlbum,
      chatRoomId: selectedGallery.ChatRoomId || activeSourceId,
      countryCode,
      intervalMs,
      sourceType,
      sourceId: activeSourceId,
    });

    // Reset form
    setJobName("");
    setDirHandle(null);
    setFolderName("");
    setSelectedEventId("");
    setSelectedRoomId("");
    setSelectedGalleryId("");
    setSelectedAlbumId("");
  };

  const handleStart = () => {
    if (!canStart) return;
    if (hasAcceptedPrivacy(user?.Email)) {
      doCreateJob();
    } else {
      setShowPrivacyModal(true);
    }
  };

  const getNextScanIn = useCallback((job) => {
    if (!job.lastScanTime || job.status !== "running") return null;
    const nextScanAt = new Date(job.lastScanTime).getTime() + job.intervalMs;
    const remaining = Math.max(0, Math.ceil((nextScanAt - Date.now()) / 1000));
    if (remaining <= 0) return "any moment...";
    if (remaining < 60) return `${remaining}s`;
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
  }, []);

  const jobList = Object.values(jobs);

  if (!loaded) {
    return (
      <div>
        <h2 style={styles.heading}>Auto Upload</h2>
        <Loader />
      </div>
    );
  }

  if (!isFileSystemSupported) {
    return (
      <div>
        <h2 style={styles.heading}>Auto Upload</h2>
        <div style={styles.warningCard}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <h3 style={{ margin: "0 0 8px", color: "#b45309" }}>Browser Not Supported</h3>
          <p style={{ margin: 0, color: "#92400e", fontSize: 14 }}>
            Auto Upload requires the File System Access API which is only available in
            <strong> Google Chrome</strong> or <strong>Microsoft Edge</strong>.
            Please open this panel in one of those browsers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showPrivacyModal && (
        <PrivacyPolicyModal
          onAccept={() => { setShowPrivacyModal(false); doCreateJob(); }}
          onDecline={() => setShowPrivacyModal(false)}
        />
      )}
    <div>
      <h2 style={styles.heading}>Auto Upload</h2>
      <PSSUpdateBanner installedVersion={pssInstalledVersion} latestVersion={pssLatestVersion} />
      <p style={styles.description}>
        Select a local folder and an album. The system will automatically scan for new
        photos & videos at the interval you set and upload them.
      </p>

      {/* Setup Card */}
      <div style={styles.setupCard}>
        <h3 style={styles.cardTitle}>New Auto Upload Job</h3>

        {/* Job Name */}
        <div style={styles.field}>
          <label style={styles.label}>Job Name (optional)</label>
          <input
            style={styles.select}
            type="text"
            placeholder="e.g. Wedding Photos, DJ Booth Cam"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
          />
        </div>

        {/* Folder Selection */}
        <div style={styles.field}>
          <label style={styles.label}>Local Folder</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Button onClick={handleSelectFolder} variant="secondary" style={{ padding: "8px 16px", fontSize: 13 }}>
              {dirHandle ? "📂 Change Folder" : "📁 Select Folder"}
            </Button>
            {folderName && (
              <span style={styles.folderName}>{folderName}</span>
            )}
          </div>
        </div>

        {/* Source Type Toggle */}
        <div style={styles.field}>
          <label style={styles.label}>Upload To</label>
          <div style={styles.toggleGroup}>
            <button
              type="button"
              style={{
                ...styles.toggleBtn,
                ...(sourceType === "event" ? styles.toggleBtnActive : {}),
              }}
              onClick={() => setSourceType("event")}
            >
              🎉 Event
            </button>
            <button
              type="button"
              style={{
                ...styles.toggleBtn,
                ...(sourceType === "room" ? styles.toggleBtnActive : {}),
              }}
              onClick={() => setSourceType("room")}
            >
              💬 @Room
            </button>
          </div>
        </div>

        {/* Event Selection */}
        {sourceType === "event" && (
          <div style={styles.field}>
            <label style={styles.label}>Event</label>
            <select
              style={styles.select}
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              disabled={loadingEvents}
            >
              <option value="">{loadingEvents ? "Loading events..." : "— Select Event —"}</option>
              {events.map((ev) => (
                <option key={ev.Id} value={ev.Id}>{ev.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Room Selection */}
        {sourceType === "room" && (
          <div style={styles.field}>
            <label style={styles.label}>Room</label>
            <select
              style={styles.select}
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              disabled={loadingRooms}
            >
              <option value="">{loadingRooms ? "Loading rooms..." : "— Select Room —"}</option>
              {rooms.map((r) => (
                <option key={r.Id} value={r.Id}>{r.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Gallery Selection */}
        <div style={styles.field}>
          <label style={styles.label}>Gallery</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={selectedGalleryId}
              onChange={(e) => setSelectedGalleryId(e.target.value)}
              disabled={!activeSourceId || loadingGalleries}
            >
              <option value="">
                {!activeSourceId
                  ? `— Select ${sourceType} first —`
                  : loadingGalleries ? "Loading..." : "— Select Gallery —"}
              </option>
              {galleries.map((g) => (
                <option key={g.Id} value={g.Id}>{g.Name}</option>
              ))}
            </select>
            {activeSourceId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateGallery(true)}
                style={styles.inlineCreateBtn}
                title="Create new gallery"
              >
                + New
              </Button>
            )}
          </div>
        </div>

        {/* Album Selection */}
        <div style={styles.field}>
          <label style={styles.label}>Album</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={selectedAlbumId}
              onChange={(e) => setSelectedAlbumId(e.target.value)}
              disabled={!selectedGalleryId || albums.length === 0}
            >
              <option value="">
                {!selectedGalleryId
                  ? "— Select gallery first —"
                  : albums.length === 0
                  ? "— No albums in this gallery —"
                  : "— Select Album —"}
              </option>
              {albums.map((a) => (
                <option key={a.Id} value={a.Id}>{a.Name} ({a.ImageCount || 0} files)</option>
              ))}
            </select>
            {selectedGalleryId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateAlbum(true)}
                style={styles.inlineCreateBtn}
                title="Create new album"
              >
                + New
              </Button>
            )}
          </div>
        </div>

        {/* Interval Selection */}
        <div style={styles.field}>
          <label style={styles.label}>Scan Interval</label>
          <select
            style={styles.select}
            value={intervalMs}
            onChange={(e) => setIntervalMs(Number(e.target.value))}
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <Button onClick={handleStart} disabled={!canStart} style={{ marginTop: 8 }}>
          🚀 Start Auto Upload
        </Button>
      </div>

      {/* Active Jobs */}
      {jobList.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={styles.cardTitle}>Active Jobs ({jobList.length})</h3>

          {jobList.map((job) => (
            <div key={job.id} style={styles.jobCard}>
              {/* Confirm Remove Dialog */}
              {confirmRemoveId === job.id && (
                <div style={styles.confirmOverlay} onClick={() => setConfirmRemoveId(null)}>
                  <div style={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
                    <h4 style={{ margin: "0 0 8px" }}>Remove this job?</h4>
                    <p style={{ margin: "0 0 16px", fontSize: 13, color: "#666" }}>
                      This will stop scanning "{job.folderName}". Already uploaded files won't be affected.
                    </p>
                    <div style={{ display: "flex", gap: 10 }}>
                      <Button variant="secondary" onClick={() => setConfirmRemoveId(null)} style={{ flex: 1 }}>
                        Keep
                      </Button>
                      <Button onClick={() => { removeJob(job.id); setConfirmRemoveId(null); }} style={{ flex: 1, background: "#e53935" }}>
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Job Header */}
              <div style={styles.jobHeader}>
                <div>
                  <div style={styles.jobTitle}>
                    <span style={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      background: job.status === "running" ? "#22c55e" : job.status === "needs-permission" ? "#ef4444" : "#eab308",
                      marginRight: 8,
                    }} />
                    {job.jobName}
                  </div>
                  <div style={styles.jobSubtitle}>
                    📂 {job.folderName} → {job.gallery.Name} / {job.album.Name}
                  </div>
                </div>
                <div style={styles.jobActions}>
                  {job.status === "running" && (
                    <>
                      <button
                        style={styles.actionBtn}
                        onClick={() => scanNow(job.id)}
                        disabled={job.scanning}
                        title="Scan now"
                      >
                        {job.scanning ? "⏳" : "🔄"}
                      </button>
                      <button style={styles.actionBtn} onClick={() => pauseJob(job.id)} title="Pause">
                        ⏸️
                      </button>
                    </>
                  )}
                  {(job.status === "paused" || job.status === "needs-permission") && (
                    <button style={styles.actionBtn} onClick={() => resumeJob(job.id)} title="Resume">
                      ▶️
                    </button>
                  )}
                  <button style={styles.actionBtn} onClick={() => setEditingJob(job)} title="Edit">
                    ✏️
                  </button>
                  <button
                    style={{ ...styles.actionBtn, color: "#e53935" }}
                    onClick={() => setConfirmRemoveId(job.id)}
                    title="Remove"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Job Stats */}
              <div style={styles.jobStats}>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>Interval</span>
                  <span style={styles.statValue}>{formatInterval(job.intervalMs)}</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>Last Scan</span>
                  <span style={styles.statValue}>{formatTime(job.lastScanTime)}</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>Next Scan</span>
                  <span style={styles.statValue}>
                    {job.status === "running" ? (getNextScanIn(job) || "soon...") : "—"}
                  </span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>New Last Scan</span>
                  <span style={styles.statValue}>{job.lastScanNewFiles ?? "—"}</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>Total Uploaded</span>
                  <span style={styles.statValue}>{job.totalUploaded}</span>
                </div>
              </div>

              {/* Status */}
              <div style={styles.jobStatus}>
                {job.scanning && <span style={styles.scanPulse}>●</span>}
                <span style={{ color: "#666", fontSize: 13 }}>{job.statusText}</span>
              </div>

              {/* Live Upload Progress */}
              {(() => {
                // Find upload sessions belonging to this job that still have active files
                const jobSessions = Object.values(sessions).filter(
                  (s) => s.id && s.id.startsWith(`auto-${job.id}-`) && s.started &&
                    (s.fileItems || []).some((f) =>
                      ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status)
                    )
                );
                if (jobSessions.length === 0 || hiddenProgress[job.id]) {
                  // Reset hidden flag if new sessions appear
                  if (jobSessions.length > 0 && hiddenProgress[job.id]) {
                    const hasActive = jobSessions.some((s) =>
                      (s.fileItems || []).some((f) =>
                        ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status)
                      )
                    );
                    if (hasActive) {
                      setHiddenProgress((p) => { const n = { ...p }; delete n[job.id]; return n; });
                      // fall through to render
                    } else {
                      return null;
                    }
                  } else {
                    return null;
                  }
                }

                const allFiles = jobSessions.flatMap((s) => s.fileItems || []);
                const activeFiles = allFiles.filter((f) =>
                  ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status)
                );
                const doneFiles = allFiles.filter((f) => f.status === "done");
                const failedFiles = allFiles.filter((f) => f.status === "failed");
                const allDone = allFiles.length > 0 && allFiles.every((f) => f.status === "done" || f.status === "failed");
                const overallPct = allFiles.length > 0
                  ? Math.round((doneFiles.length / allFiles.length) * 100)
                  : 0;

                // Auto-hide 3s after all done
                if (allDone && !hiddenProgress[job.id]) {
                  setTimeout(() => setHiddenProgress((p) => ({ ...p, [job.id]: true })), 3000);
                }

                return (
                  <div style={styles.progressSection}>
                    {/* Overall progress bar */}
                    <div style={styles.overallRow}>
                      <span style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>
                        {doneFiles.length}/{allFiles.length} files
                        {failedFiles.length > 0 && ` (${failedFiles.length} failed)`}
                      </span>
                      <span style={{ fontSize: 12, color: "#6c63ff", fontWeight: 700 }}>{overallPct}%</span>
                    </div>
                    <div style={styles.overallBarBg}>
                      <div style={{ ...styles.overallBarFill, width: `${overallPct}%` }} />
                    </div>

                    {/* Active file details */}
                    {activeFiles.slice(0, 5).map((item) => (
                      <div key={item.id} style={styles.fileRow}>
                        <span style={{ fontSize: 14 }}>{statusIcon(item.status, item.fileType)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.fileName}>{item.fileName}</div>
                          <div style={styles.fileInfo}>
                            {item.originalSize
                              ? <>{formatFileSize(item.originalSize)}<span style={{ color: "#bbb" }}> → </span><span style={{ color: "#16a34a", fontWeight: 700 }}>{formatFileSize(item.fileSize)}</span>{item.compressSec && <span style={{ color: "#9ca3af" }}> ({item.compressSec}s)</span>}</>
                              : formatFileSize(item.fileSize)
                            }{" — "}{fileStatusText(item)}
                          </div>
                          {(item.status === "uploading" || item.status === "compressing") && (
                            <div style={styles.fileBarBg}>
                              <div style={{
                                ...styles.fileBarFill,
                                width: `${item.status === "compressing" ? item.compressProgress : item.uploadProgress}%`,
                                background: item.status === "compressing" ? "#f59e0b" : "#6c63ff",
                              }} />
                            </div>
                          )}
                          {item.compressEngine && (
                            <span style={{
                              display: "inline-block",
                              marginTop: 2,
                              fontSize: 10,
                              fontWeight: 600,
                              color: item.compressEngine === "libx264" ? "#888" : "#6c63ff",
                              background: item.compressEngine === "libx264" ? "#f3f4f6" : "#ede9fe",
                              borderRadius: 4,
                              padding: "1px 6px",
                            }}>
                              {encoderLabel(item.compressEngine)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {activeFiles.length > 5 && (
                      <div style={{ fontSize: 12, color: "#888", padding: "4px 0" }}>
                        +{activeFiles.length - 5} more in queue
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {jobList.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
          <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
            No auto-upload jobs running. Set one up above to get started!
          </p>
        </div>
      )}

      {/* Create Gallery Modal */}
      {showCreateGallery && (
        <CreateGalleryModal
          eventId={activeSourceId}
          onClose={() => setShowCreateGallery(false)}
          onCreated={(newGallery) => {
            // Refetch galleries and auto-select the new one
            setLoadingGalleries(true);
            getGalleriesByEvent(activeSourceId)
              .then((res) => {
                const updated = res.data?.Result || [];
                setGalleries(updated);
                // Find the newly created gallery and select it
                const created = updated.find((g) => g.Name === newGallery?.Name) || updated[updated.length - 1];
                if (created) setSelectedGalleryId(String(created.Id));
              })
              .catch(() => toast.error("Failed to refresh galleries"))
              .finally(() => setLoadingGalleries(false));
          }}
        />
      )}

      {/* Create Album Modal */}
      {showCreateAlbum && selectedGallery && (
        <CreateAlbumModal
          galleryId={selectedGallery.Id}
          galleryName={selectedGallery.Name}
          onClose={() => setShowCreateAlbum(false)}
          onCreated={(newAlbum) => {
            // Refetch galleries to get updated album list and auto-select the new album
            setLoadingGalleries(true);
            getGalleriesByEvent(activeSourceId)
              .then((res) => {
                const updated = res.data?.Result || [];
                setGalleries(updated);
                const updatedGallery = updated.find((g) => String(g.Id) === String(selectedGalleryId));
                if (updatedGallery) {
                  const created = (updatedGallery.Albums || []).find((a) => a.Name === newAlbum?.Name)
                    || (updatedGallery.Albums || [])[(updatedGallery.Albums || []).length - 1];
                  if (created) setSelectedAlbumId(String(created.Id));
                }
              })
              .catch(() => toast.error("Failed to refresh albums"))
              .finally(() => setLoadingGalleries(false));
          }}
        />
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={(updates) => {
            updateJob(updates);
            setEditingJob(null);
          }}
        />
      )}
    </div>
    </>
  );
};

/* ─── Styles ─── */
const styles = {
  heading: { margin: "0 0 8px", fontSize: 22, fontWeight: 700, color: "#1e1e2d" },
  description: { margin: "0 0 24px", fontSize: 14, color: "#666", lineHeight: 1.6 },

  setupCard: {
    background: "#fff", borderRadius: 12, padding: 28,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", maxWidth: 560,
  },
  cardTitle: { margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#1e1e2d" },

  field: { marginBottom: 18 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6 },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0",
    fontSize: 14, color: "#333", background: "#fafafa", outline: "none",
    cursor: "pointer", appearance: "auto",
  },
  folderName: {
    fontSize: 14, color: "#6c63ff", fontWeight: 600,
    padding: "6px 12px", background: "#f3f0ff", borderRadius: 8,
  },
  inlineCreateBtn: {
    padding: "8px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
    flexShrink: 0,
  },
  toggleGroup: {
    display: "flex", gap: 0, borderRadius: 8, overflow: "hidden",
    border: "1.5px solid #e0e0e0", width: "fit-content",
  },
  toggleBtn: {
    padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
    border: "none", background: "#fafafa", color: "#666",
    transition: "all 0.15s ease",
  },
  toggleBtnActive: {
    background: "#6c63ff", color: "#fff",
  },

  warningCard: {
    background: "#fffbeb", border: "1.5px solid #fbbf24", borderRadius: 12,
    padding: 28, maxWidth: 500, textAlign: "center",
  },

  jobCard: {
    position: "relative", background: "#fff", borderRadius: 12, padding: 20,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 16,
  },
  jobHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16,
  },
  jobTitle: { fontSize: 15, fontWeight: 700, color: "#1e1e2d", display: "flex", alignItems: "center" },
  jobSubtitle: { fontSize: 13, color: "#888", marginTop: 4, paddingLeft: 16 },
  jobActions: { display: "flex", gap: 6 },
  actionBtn: {
    background: "none", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1,
  },

  jobStats: {
    display: "flex", gap: 16, flexWrap: "wrap", padding: "12px 0",
    borderTop: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0",
  },
  stat: { display: "flex", flexDirection: "column", gap: 2, minWidth: 80 },
  statLabel: { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 },
  statValue: { fontSize: 14, fontWeight: 600, color: "#1e1e2d" },

  jobStatus: { display: "flex", alignItems: "center", gap: 8, marginTop: 12 },
  scanPulse: { color: "#22c55e", animation: "pulse 1.5s infinite", fontSize: 12 },

  confirmOverlay: {
    position: "absolute", inset: 0, background: "rgba(255,255,255,0.9)",
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: 12, zIndex: 10,
  },
  confirmBox: {
    background: "#fff", borderRadius: 12, padding: 24,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)", maxWidth: 320, textAlign: "center",
  },

  emptyState: {
    textAlign: "center", padding: "48px 0",
  },

  /* Upload progress styles */
  progressSection: {
    marginTop: 14, padding: "14px 0 0",
    borderTop: "1px solid #f0f0f0",
  },
  overallRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6,
  },
  overallBarBg: {
    height: 6, borderRadius: 3, background: "#f0f0f0", overflow: "hidden", marginBottom: 12,
  },
  overallBarFill: {
    height: "100%", borderRadius: 3, background: "#6c63ff",
    transition: "width 0.3s ease",
  },
  fileRow: {
    display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0",
    borderBottom: "1px solid #fafafa",
  },
  fileName: {
    fontSize: 13, fontWeight: 600, color: "#333",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  fileInfo: { fontSize: 12, color: "#888", marginTop: 2 },
  fileBarBg: {
    height: 4, borderRadius: 2, background: "#f0f0f0", overflow: "hidden", marginTop: 4,
  },
  fileBarFill: {
    height: "100%", borderRadius: 2, transition: "width 0.3s ease",
  },
};

export default AutoUpload;
